import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { describeError } from "@/api/client";
import { useCanEdit } from "@/api/session";
import {
  Button,
  DataTable,
  EmptyState,
  Icon,
  RelativeTime,
  Select,
  TextField,
  Toolbar,
  useToast,
  type DataTableColumn,
  type SelectOption,
} from "@/components";
import { UsageRangeBar, usageSummary, type UsageStat } from "@/features/materials/api";
import { formatInteger, formatPercentNumber } from "@/lib/format";
import { useDebouncedValue, useSearchParamsInPlace } from "@/lib/hooks";
import { useRecomputeUsage, useUsageStats, type UsageSort, type UsageSource } from "./api";
import styles from "./KnowledgePage.module.css";

const PAGE_SIZE = 50;

const SOURCE_LABELS: Record<UsageSource, string> = {
  demo_formulas: "TGSC demo formulas",
  patents: "Patent examples",
  workspace: "Your formulas",
};

const SOURCE_OPTIONS: ReadonlyArray<SelectOption> = [
  { value: "all", label: "All sources" },
  { value: "demo_formulas", label: SOURCE_LABELS.demo_formulas },
  { value: "workspace", label: SOURCE_LABELS.workspace },
];

const SORT_OPTIONS: ReadonlyArray<SelectOption<UsageSort>> = [
  { value: "-n", label: "Most formulas" },
  { value: "name", label: "Material A–Z" },
  { value: "-median", label: "Highest median" },
  { value: "median", label: "Lowest median" },
];

function isSource(value: string | null): value is UsageSource {
  return value === "demo_formulas" || value === "patents" || value === "workspace";
}

function isSort(value: string | null): value is UsageSort {
  return SORT_OPTIONS.some((option) => option.value === value);
}

function pct(value: string | null): string {
  return `${formatPercentNumber(value, { minDecimals: 0, maxDecimals: 3 })}\u00a0%`;
}

/** Typical neat % per material across demo formulas and your released formulas. */
export function UsageTab() {
  const [searchParams, setSearchParams] = useSearchParamsInPlace();
  const navigate = useNavigate();
  const canEdit = useCanEdit();
  const toast = useToast();
  const recompute = useRecomputeUsage();
  const [searchText, setSearchText] = useState(searchParams.get("q") ?? "");
  const query = useDebouncedValue(searchText.trim(), 250);
  const sourceParam = searchParams.get("source");
  const sortParam = searchParams.get("sort");
  const [page, setPage] = useState(0);
  const params = {
    q: query || undefined,
    source_kind: isSource(sourceParam) ? sourceParam : undefined,
    sort: isSort(sortParam) ? sortParam : undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };
  const stats = useUsageStats(params);

  function setParam(name: string, value: string | null) {
    setPage(0);
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (!value || value === "all") next.delete(name);
        else next.set(name, value);
        return next;
      },
      { replace: true },
    );
  }

  function startRecompute() {
    recompute.mutate(undefined, {
      onSuccess: () =>
        toast.info("Recomputing usage ranges", {
          description: "The worker updates them in a moment.",
          action: { label: "View jobs", onClick: () => void navigate("/jobs") },
        }),
      onError: (error) => toast.error("Couldn’t start the recompute", { description: describeError(error) }),
    });
  }

  const columns: DataTableColumn<UsageStat>[] = [
    {
      id: "material",
      header: "Material",
      width: "wide",
      cell: (row) => (
        <Link to={`/materials/${row.material_id}`} className={styles.materialLink}>
          {row.material_name}
        </Link>
      ),
    },
    {
      id: "source",
      header: "From",
      width: "fit",
      cell: (row) => SOURCE_LABELS[row.source_kind],
    },
    { id: "n", header: "Formulas", numeric: true, width: "fit", cell: (row) => formatInteger(row.n_formulas) },
    {
      id: "range",
      header: "Middle half, median and maximum",
      cell: (row) => (
        <span className={styles.rangeCell}>
          <UsageRangeBar range={row} />
          <span className={styles.subtle}>
            {pct(row.p25_pct)}–{pct(row.p75_pct)}
          </span>
        </span>
      ),
    },
    { id: "median", header: "Median", numeric: true, width: "fit", cell: (row) => pct(row.median_pct) },
    { id: "max", header: "Maximum", numeric: true, width: "fit", cell: (row) => pct(row.max_pct) },
    { id: "computed", header: "Computed", width: "fit", cell: (row) => <RelativeTime value={row.computed_at} /> },
  ];

  const total = stats.data?.count ?? 0;
  return (
    <div className={styles.panel}>
      <Toolbar
        label="Filter usage ranges"
        end={
          canEdit ? (
            <Button icon="refresh" pending={recompute.isPending} pendingLabel="Starting…" onClick={startRecompute}>
              Recompute
            </Button>
          ) : null
        }
      >
        <TextField
          label="Material"
          hideLabel
          size="sm"
          type="search"
          className={styles.search}
          placeholder="Find a material"
          prefix={<Icon name="search" />}
          value={searchText}
          onValueChange={(value) => {
            setSearchText(value);
            setPage(0);
          }}
        />
        <Select
          label="From"
          size="sm"
          options={SOURCE_OPTIONS}
          value={params.source_kind ?? "all"}
          onValueChange={(value) => setParam("source", value)}
        />
        <Select
          label="Sort"
          size="sm"
          options={SORT_OPTIONS}
          value={params.sort ?? "-n"}
          onValueChange={(value) => setParam("sort", value === "-n" ? null : value)}
        />
      </Toolbar>
      <DataTable
        caption="Usage ranges"
        columns={columns}
        rows={stats.data?.items}
        getRowId={(row) => row.id}
        loading={stats.isPending}
        error={stats.error}
        onRetry={() => void stats.refetch()}
        empty={
          <EmptyState
            variant="inline"
            headingLevel={3}
            icon="flask"
            title={query ? `No usage range for “${query}”` : "No usage ranges yet"}
            description="Ranges are computed from TGSC demo formulas (as the TGSC crawl fetches them) and from your released formulas."
          />
        }
      />
      {stats.data?.items.length ? (
        <p className={styles.footnote}>
          The bar spans the middle half of the doses on a log scale from 0.001 % to 100 %; the tick is the median and
          the dot the maximum. For example, {usageSummary(stats.data.items[0]!).toLowerCase()}.
        </p>
      ) : null}
      {total > PAGE_SIZE ? (
        <nav className={styles.pager} aria-label="Pages">
          <span className={styles.pagerText}>
            {formatInteger(page * PAGE_SIZE + 1)}–{formatInteger(Math.min(total, (page + 1) * PAGE_SIZE))} of{" "}
            {formatInteger(total)}
          </span>
          <Button size="sm" icon="chevronLeft" disabled={page === 0} onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <Button
            size="sm"
            iconEnd="chevronRight"
            disabled={(page + 1) * PAGE_SIZE >= total}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </nav>
      ) : null}
    </div>
  );
}
